import { Query } from '@varo/graphql-schema-types';
import { useCallback, useEffect, useRef } from 'react';
import FetchUser from 'services/graphql/queries/FetchUser';
import { AbortError } from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import useCancellableQuery from 'shared/hooks/useCancellableQuery/useCancellableQuery';
import fetchAddressCoords from 'shared/utils/maps/fetchAddressCoords';

export interface LocationCoordinates {
  lat: number;
  lng: number;
}

type GeocodeDetailsResponse = {
  results?: {
    geometry?: {
      location?: {
        lat?: string;
        lng?: string;
      };
    };
  }[];
};

const parseCoordinates = (
  result: GeocodeDetailsResponse,
): LocationCoordinates | null => {
  const location = result.results?.[0]?.geometry?.location;

  if (!location?.lat || !location.lng) {
    return null;
  }

  const lat = Number.parseFloat(location.lat);
  const lng = Number.parseFloat(location.lng);

  if (Number.isNaN(lat) || Number.isNaN(lng)) {
    return null;
  }

  return { lat, lng };
};

interface GetZipCodeCoordinatesOptions {
  onMissingZipCode?: () => void;
}

const fetchGeocodeDetails = async (
  zip: string,
  mapsUrl: string,
  signal: AbortSignal,
): Promise<GeocodeDetailsResponse> => {
  try {
    const response = await fetchAddressCoords(zip, mapsUrl, signal);
    const result = (await response.json()) as GeocodeDetailsResponse;

    if (signal.aborted) {
      throw new AbortError();
    }

    return result;
  } catch (error) {
    if (signal.aborted) {
      throw new AbortError();
    }
    throw error;
  }
};

export const useZipCodeCoordinates = () => {
  const [executeFetchUserQuery] = useCancellableQuery<Query>(FetchUser);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const getZipCodeCoordinates = useCallback(
    async (
      mapsUrl: string,
      { onMissingZipCode }: GetZipCodeCoordinatesOptions = {},
    ): Promise<LocationCoordinates | null> => {
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const userData = await executeFetchUserQuery(
        {},
        { requestPolicy: 'cache-first' },
      );
      const zip = userData.user?.customerInfo?.address?.zip;

      if (!zip) {
        onMissingZipCode?.();
        return null;
      }

      const result = await fetchGeocodeDetails(
        zip,
        mapsUrl,
        abortController.signal,
      );

      return parseCoordinates(result);
    },
    [executeFetchUserQuery],
  );

  return getZipCodeCoordinates;
};

export default useZipCodeCoordinates;
